import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TooltipProps } from "recharts";
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import type { Payload } from "recharts/types/component/DefaultLegendContent";
import type { AnimationTiming } from "recharts/types/util/types";
import { cn } from "senf-atomic-design-system/src/util/classNameConstructor";
import { getAlpha } from "senf-shared/utils/helpers";
import { useDimensions } from "senf-shared/utils/hooks";
import Icon from "../../icon/Icon";
import { ChartTooltip } from "../ChartTooltip";
import { Legend as CustomLegend } from "../Legend";
import {
  getColor,
  getCosSin,
  percentageFormatter,
  sortChartData,
} from "../shared/helpers";
import { renderTooltipContent, type TooltipConfig } from "../shared/Tooltip";
import type {
  BaseChartProps,
  GroupKey,
  PieSectorProps,
  Position,
  SurveyChartPoint,
} from "../shared/types";

// Helper functions for label positioning
const calculatePositions = (
  { midAngle, outerRadius, cx, cy, ...props }: PieSectorProps,
  {
    defaultDistance,
    positions,
    setPositions,
    labelHeight = 30,
    containerWidth,
  }: {
    defaultDistance: number;
    positions: React.RefObject<Map<number, Position>>;
    setPositions?: boolean;
    labelHeight?: number;
    containerWidth: number;
  },
): Position => {
  const { sin, cos } = getCosSin(midAngle);
  const sx = cx + (outerRadius - 1) * cos;
  const sy = cy + (outerRadius - 1) * sin;
  const previousPosition = positions.current.get(props.index - 1);
  const right =
    cos >= 0 ||
    (sin < 0 && cos >= -0.05 && (previousPosition?.cos || 1) >= -0.2);
  const tempMy = right
    ? 0
    : cy - (outerRadius + defaultDistance) * Math.abs(sin);
  const labelDistance = 5;
  const spacingAddition =
    previousPosition?.right === right
      ? tempMy -
        (previousPosition.my -
          previousPosition.labelHeight / 2 -
          labelDistance -
          labelHeight / 2)
      : 0;

  const distance = Math.max(
    Math.min(300, defaultDistance + spacingAddition),
    Math.abs(sin) * (labelHeight / 2),
  );

  const mx = cx + (outerRadius + distance) * cos;
  const my = cy + (outerRadius + distance) * sin;
  const d = sin !== 0 ? -distance / sin : 0;

  const ex = mx + (right ? 1 : -1) * defaultDistance;
  const textAnchor = right ? "start" : "end";
  const maxWidth = Math.max((right ? containerWidth - ex : ex) - 10, 0);

  const calculations = {
    cx,
    cy,
    sx,
    sy,
    mx,
    my,
    d,
    cos,
    sin,
    midAngle,
    distance,
    right,
    labelHeight,
    textAnchor,
    ex,
    maxWidth,
    offsetTop: my - sy - labelHeight / 2,
    offsetBottom: my - sy + labelHeight / 2,
  };

  if (setPositions) positions.current.set(props.index, calculations);

  return calculations;
};

interface PieChartProps extends BaseChartProps {
  data: SurveyChartPoint[];
  innerRadius?: number;
  outerRadius?: number;
  legendReference?: boolean;
  showLabels?: boolean;
  labelType?: "internal" | "external" | "none";
  height?: number | string;
  width?: number | string;
  colorType?: "sequential" | "categorical";
  animation?: boolean;
  center?: {
    label?: string;
    value: string;
  };
  baseColor?: string;
  groupKeys?: GroupKey[];
  tooltipConfig?: TooltipConfig;
  classNames?: {
    legend?: string;
  };
  legend?: boolean;
  isLoading?: boolean;
  labelSize?: "md" | "sm";
}
const getLabelHeight = (
  { label }: { label: string },
  maxWidth: number,
  maxLines = 5,
) => {
  const words = label.split(" ");
  const testSpan = document.createElement("span");
  testSpan.className = "text-sm";
  testSpan.style.position = "absolute";
  testSpan.style.visibility = "hidden";
  document.body.appendChild(testSpan);

  const lines: string[] = [""];
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = lines[lines.length - 1]
      ? `${lines[lines.length - 1]} ${word}`
      : word;

    testSpan.textContent = testLine;
    const testWidth = testSpan.getBoundingClientRect().width;

    if (testWidth <= maxWidth) {
      lines[lines.length - 1] = testLine;
    } else if (lines.length < maxLines) {
      lines.push(word);
    } else {
      // Try truncating with ellipsis
      testSpan.textContent = `${lines[lines.length - 1]}...`;
      if (testSpan.getBoundingClientRect().width <= maxWidth) {
        lines[lines.length - 1] += "...";
      } else {
        // If even ellipsis doesn’t fit, truncate last word char-wise
        let truncated = lines[lines.length - 1];
        while (truncated.length > 0) {
          testSpan.textContent = `${truncated}...`;
          if (testSpan.getBoundingClientRect().width <= maxWidth) {
            lines[lines.length - 1] = `${truncated}...`;
            break;
          }
          truncated = truncated.slice(0, -1);
        }
      }
      break;
    }
  }

  document.body.removeChild(testSpan);
  return lines.filter((line) => line.trim() !== "");
};

// Separate label renderer function that doesn't update state
const labelRenderer = (
  props: {
    cx: number;
    cy: number;
    midAngle: number;
    outerRadius: number;
    value: number;
    percent: number;
    name: string;
    fill: string;
    lineColor: string;
    index: number;
    startAngle?: number;
    endAngle?: number;
    label?: string;
  },
  positions: React.RefObject<Map<number, Position>>,
  options: {
    centerRef: React.RefObject<{ x: number; y: number }>;
    dimensions: { width: number; height: number };
    size: "md" | "sm";
  },
) => {
  if (props.percent < 0.005) return null;
  const offsetDistance = 10;
  const value = percentageFormatter(props.percent);
  const label = (props.label || props.name).trim();

  // Create a props object that matches PieSectorProps interface
  const sectorProps: PieSectorProps = {
    ...props,
    startAngle: props.startAngle ?? 0,
    endAngle: props.endAngle ?? 0,
  };

  const firstPass = calculatePositions(sectorProps, {
    defaultDistance: offsetDistance,
    labelHeight: 30,
    positions,
    containerWidth: options.dimensions.width - 10,
  });

  options.centerRef.current = { x: sectorProps.cx, y: sectorProps.cy };

  const LINE_HEIGHT = options.size === "sm" ? 13 : 15;
  const lines = getLabelHeight({ label }, firstPass.maxWidth - 30);
  const labelHeight = lines.length * LINE_HEIGHT;
  const blockHeight = LINE_HEIGHT + labelHeight + 5;

  console.log("lines", lines);

  const { sx, sy, mx, my, right, textAnchor } = calculatePositions(
    sectorProps,
    {
      defaultDistance: offsetDistance,
      containerWidth: options.dimensions.width,
      labelHeight: blockHeight,
      positions,
      setPositions: true,
    },
  );

  const ex = mx + (right ? 1 : -1) * offsetDistance;
  const labelPosition = {
    x: Math.max(ex + (right ? 10 : -10), 0),
    y: Math.max(my - blockHeight / 2 + LINE_HEIGHT, 0),
  };

  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${my}`}
        stroke={props.lineColor || "var(--border-default)"}
        strokeWidth={1}
        fill="none"
      />
      <circle
        cx={ex}
        cy={my}
        r={2}
        fill={props.fill || "var(--border-default)"}
        stroke={props.lineColor || "var(--border-default)"}
        strokeWidth={1}
      />
      <text
        x={labelPosition.x}
        y={labelPosition.y}
        textAnchor={textAnchor}
        className={cn(
          "font-bold fill-text-default whitespace-nowrap",
          options.size === "sm" ? "text-sm" : "text-md",
        )}
      >
        {value}
      </text>
      {lines.map((line, index) => {
        return (
          <text
            x={labelPosition.x}
            y={labelPosition.y + LINE_HEIGHT + index * LINE_HEIGHT}
            textAnchor={textAnchor}
            className={cn(
              "opacity-50",
              options.size === "sm" ? "text-xs" : "text-sm",
            )}
            key={`${line}-${index}`}
          >
            {line}
          </text>
        );
      })}
    </g>
  );
};

const PieChart: React.FC<PieChartProps> = ({
  data,
  className = "",
  innerRadius = 50,
  outerRadius = 80,
  legendReference = false,
  center,
  baseColor,
  groupKeys,
  tooltipConfig,
  classNames,
  isLoading,
  legend = true,
  labelSize = "md",
}) => {
  const { t } = useTranslation();
  // Check if this is grouped data
  const isGrouped = groupKeys && groupKeys.length > 0;

  // Convert data to pie chart format - matching ComposedChart pattern
  const pieData = useMemo(() => {
    if (data.length === 0) return [];

    // Handle survey format - just return as-is if it has the right structure
    if (data[0] && "name" in data[0] && "value" in data[0]) {
      const surveyData = data as SurveyChartPoint[];
      return sortChartData(surveyData).map((item, index) => {
        // Create a dummy ChartPoint array for getColor compatibility (matching ComposedChart)
        const dummyArray = surveyData.map((_, i) => ({
          x: i.toString(),
          y: 0,
          value: 0,
          percent: 0,
        }));

        // Use item.color/item.fill first, then groupKeys color, then default color logic
        let fillColor: string;
        let strokeColor: string;

        let label = String(item.label);
        let groupLabel = String(item.name);

        // Prioritize item's own colors
        if (item.color || item.fill) {
          fillColor = String(item.fill || item.color || "");
          strokeColor = String(item.fill || item.color || "");
        } else if (isGrouped && groupKeys) {
          // Find the group that matches this item's name or use the first group as fallback
          const matchingGroup =
            groupKeys.find((group) => group.key === item.name) || groupKeys[0];
          fillColor = matchingGroup.color;
          strokeColor = matchingGroup.color;
          groupLabel = matchingGroup.label;
          label = matchingGroup.label;
        } else {
          const colors = getColor(index, dummyArray, baseColor);
          fillColor = colors.fill;
          strokeColor = colors.stroke;
        }

        // For grouped data, include all groupKeys values in the payload for tooltip
        const groupedData =
          isGrouped && groupKeys
            ? groupKeys.reduce(
                (acc, group) => {
                  acc[group.key] = Number(item[group.key]) || 0;
                  return acc;
                },
                {} as Record<string, number>,
              )
            : {};

        return {
          fill:
            String(item.name).toLowerCase() === "other" ? "#cccccc" : fillColor,
          color:
            String(item.name).toLowerCase() === "other" ? "#cccccc" : fillColor,
          lineColor:
            String(item.name).toLowerCase() === "other"
              ? "#cccccc"
              : strokeColor,
          name: String(item.name), // Keep original name for reference
          label:
            label === "Leitstelle"
              ? "Leitstelle (n = 1) Keep original name for reference, Include grouped data for tooltip"
              : label,
          groupLabel,
          value: item.value || 0,
          count: item.count,
          percent: item.percent,
          opacity: 1,
          // Include grouped data for tooltip
          ...groupedData,
        };
      });
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isGrouped, groupKeys, baseColor]);

  const [containerRef, dimensions] = useDimensions<HTMLDivElement>();

  // Compute the largest label length.
  const largestCharLength: number = useMemo(
    () =>
      data.reduce((max: number, item: any) => {
        return Math.max(max, String(item.label).length);
      }, 0),
    [data],
  );
  const breakpoint: number = largestCharLength * 30;
  const showLegend: boolean = useMemo(
    () =>
      data.some((item) => String(item.label).length > 50) &&
      dimensions.width < breakpoint,
    [data, dimensions.width, breakpoint],
  );

  console.log("showLegend", showLegend, dimensions.width, breakpoint);
  // Create legend payload from data - matching ComposedChart pattern
  const legendPayload: Payload[] = useMemo(() => {
    if (isGrouped && groupKeys) {
      return groupKeys.map((group) => ({
        value: group.label,
        color: group.color,
      }));
    }

    return pieData.map((item, index) => {
      return {
        value: item.name,
        label:
          showLegend && legendReference
            ? getAlpha(item.initialOrder ?? index)
            : item.label || "",
        color: item.fill || item.color,
        initialOrder: index,
      };
    });
  }, [pieData, isGrouped, groupKeys]);

  // Use ref to collect distances during render without causing re-renders
  const centerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const positions = useRef(new Map<number, Position>());

  // Create a stable callback for the label renderer
  const renderLabel = useCallback(
    (props: {
      cx: number;
      cy: number;
      midAngle: number;
      outerRadius: number;
      percent: number;
      value: number;
      name: string;
      index: number;
    }) => {
      // Find the corresponding pie data item to get fill and lineColor
      const pieItem = pieData[props.index];
      const enhancedProps = {
        ...props,
        fill: pieItem?.fill || "#cccccc",
        lineColor: pieItem?.lineColor || "#cccccc",
        label:
          showLegend && legendReference
            ? getAlpha(pieItem.initialOrder ?? props.index)
            : pieItem.label || pieItem?.name,
        // label: pieItem?.label || pieItem?.name,
        // name is already displayName from the data
      };

      return labelRenderer(enhancedProps, positions, {
        centerRef,
        dimensions,
        size: labelSize,
      });
    },
    [pieData, dimensions],
  );

  // Calculate margins based on label positioning
  const [refreshMargins, setRefreshMargins] = useState<number>(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to refresh the margins when the data or dimensions change
  useEffect(() => {
    const id = setTimeout(() => setRefreshMargins((e) => e + 1), 1);
    return () => clearTimeout(id);
  }, [data.length, dimensions.width]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to refresh the margins when the refreshMargins changes
  const margin = useMemo(() => {
    const vals = Array.from(positions.current.values());
    const { tops, bottoms } = vals.reduce(
      (acc, curr) => {
        acc.tops.push(curr.offsetTop);
        acc.bottoms.push(curr.offsetBottom);
        return acc;
      },
      { tops: [] as number[], bottoms: [] as number[] },
    );

    const top = Math.max(tops.length ? Math.abs(Math.min(0, ...tops)) : 0, 50);
    const bottom = Math.max(bottoms.length ? Math.max(0, ...bottoms) : 0, 50);

    return { top, right: 0, bottom, left: 0 };
  }, [refreshMargins]);

  const lastActiveSection = useRef<number>(-1);
  const [activeSection, setActiveSection] = useState<number>(-1);
  const currentSection = useMemo(() => {
    return activeSection > -1 ? activeSection : lastActiveSection.current;
  }, [activeSection]);
  useEffect(() => {
    if (activeSection !== -1) {
      lastActiveSection.current = activeSection;
    }
  }, [activeSection]);

  // Memoize the tooltip content function to prevent re-mounting
  const tooltipContent = useCallback(
    (props: TooltipProps<number, string>) => {
      const activeIndex = props.active
        ? pieData.findIndex((item) => item.name === props.payload?.[0]?.name)
        : -1;
      const position = positions.current.get(activeIndex);
      setActiveSection(activeIndex);
      return renderTooltipContent(
        { payload: props.payload as any },
        {
          vertical: false,
          groupKeys: isGrouped ? groupKeys : undefined,
          config: tooltipConfig,
          containerWidth: dimensions.width,
          className: cn(
            "duration-[1500] transition-all ease-out-expressive -translate-y-1/2 max-w-64",
            position?.right ? "translate-x-0" : "-translate-x-full",
          ),
        },
      );
    },
    [pieData, isGrouped, groupKeys, tooltipConfig, dimensions.width],
  );

  // Memoize the tooltip position to prevent unnecessary re-renders
  const tooltipPosition = useMemo(() => {
    const { mx, my, right } = positions.current.get(currentSection) || {};
    return {
      x: (mx || dimensions.width / 2) + (right ? 8 : -8),
      y: my,
    };
  }, [currentSection, dimensions.width]);

  if (!pieData?.length || isLoading) {
    return (
      <div
        className={cn(
          "text-text-subdued w-full h-[200px] flex flex-col gap-2 items-center justify-center bg-surface-disabled rounded-lg",
          className,
        )}
        // style={{ height: `${height + 30}px` }}
      >
        <Icon
          name={isLoading ? "loading" : "statistics-pie-x-alt"}
          className="text-icon-subdued text-4xl w-[1em] h-[1em]"
        />
        <span>
          {isLoading
            ? t("global:loading")
            : t("util:nothingToShowYet", { val: t("global:data") })}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "[&_.recharts-legend-item-text]:text-xs [&_.recharts-legend-item-text]:!text-text-subdued [&_:is(g,path)]:!outline-none",
        Array.from(positions.current.values()).length > 0
          ? "opacity-100 duration-200"
          : "opacity-0",
        className,
      )}
      style={{
        width: "100%",
        minHeight: Math.max(200, outerRadius * 2 + margin.top + margin.bottom),
      }}
    >
      <ResponsiveContainer
        width="100%"
        height={Math.max(200, outerRadius * 2 + margin.top + margin.bottom)}
        ref={containerRef}
      >
        <RechartsPieChart margin={margin}>
          {center && (
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                fill: "#333",
              }}
            >
              {center.value}
              {center.label && (
                <tspan
                  x="50%"
                  dy="20"
                  style={{
                    fontSize: "12px",
                    fontWeight: "normal",
                    fill: "#666",
                  }}
                >
                  {center.label}
                </tspan>
              )}
            </text>
          )}

          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            startAngle={90}
            endAngle={-270}
            labelLine={false}
            outerRadius={outerRadius}
            innerRadius={innerRadius}
            dataKey="value"
            animationDuration={1000}
            animationEasing={
              "cubic-bezier(.1, 1, .1, 1)" as unknown as AnimationTiming
            }
            cornerRadius={5}
            label={renderLabel}
            isAnimationActive={false} // <- keeps labels stable
            paddingAngle={1.8}
          >
            {pieData.map((entry, index: number) => {
              return (
                <Cell
                  key={`cell-${entry.name}-${index}`}
                  fill={entry.fill || entry.color}
                  strokeWidth={0}
                  // strokeWidth={1.8}
                  className={cn(
                    "duration-200 transition-opacity",
                    activeSection === -1 || activeSection === index
                      ? "opacity-100"
                      : "opacity-50",
                  )}
                />
              );
            })}
          </Pie>

          {/* <Tooltip
            animationDuration={1500}
            animationEasing={
              "cubic-bezier(.1, 1, .1, 1)" as unknown as AnimationTiming
            }
            content={tooltipContent}
            allowEscapeViewBox={{
              y: true,
              x: true,
            }}
            position={tooltipPosition}
            
          /> */}
          <Tooltip
            content={<ChartTooltip />}
            payload={data}
            allowEscapeViewBox={{
              y: true,
            }}
            cursor={{ fill: "var(--action-secondary-default)" }}
            wrapperStyle={{ zIndex: 999 }}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
      {legend && showLegend && (
        <CustomLegend
          payload={legendPayload}
          className={cn("mt-5 flex justify-center w-full", classNames?.legend)}
        />
      )}
    </div>
  );
};

export default PieChart;
